import {
  type ActualDuration,
  type Decision,
  HistoryEventSchema,
  paginatedResponseSchema,
  type PermissionRequest,
  type PermissionRequestSummary,
  type RequestCancel,
  type RequestCreate,
  RequestSchema,
  type RequestStatus,
  type ReassignApprover,
  RequestSummarySchema,
  type RequestUpdate,
} from '@gestion-permisos/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';
import { ifMatch, type IntentKey, useIntentKey, withIntent } from '../api/intent.js';
import { ApiProblem } from '../api/problem.js';
import { postDocument } from './documents-api.js';

const RequestPageSchema = paginatedResponseSchema(RequestSummarySchema);
const HistorySchema = z.array(HistoryEventSchema);

/** Tamaño de página de los listados de solicitudes (la API admite hasta 100). */
export const MY_REQUESTS_PAGE_SIZE = 20;

/**
 * Ámbitos de listado que usa la web: propias (W2), como aprobador almacenado (W4), del equipo directo (W5) y
 * todas, para el administrador (W9).
 */
export type ListScope = 'mine' | 'approvals' | 'team' | 'all';

/** Filtros de H-02, como texto. Vacío no se envía. `areaId` solo lo usa el listado administrativo. */
export interface RequestPageQuery {
  readonly status?: RequestStatus | undefined;
  readonly q?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly permissionTypeId?: string | undefined;
  readonly areaId?: string | undefined;
  readonly sort?: string | undefined;
  readonly page?: number | undefined;
}

function listPath(query: RequestPageQuery & { scope?: ListScope; pageSize: number }): string {
  const params = new URLSearchParams({ scope: query.scope ?? 'mine' });
  const set = (name: string, value: string | undefined) => {
    const trimmed = value?.trim();
    if (trimmed) params.set(name, trimmed);
  };
  set('status', query.status);
  set('q', query.q);
  set('from', query.from);
  set('to', query.to);
  set('permissionTypeId', query.permissionTypeId);
  set('areaId', query.areaId);
  set('sort', query.sort);
  if (query.page !== undefined) params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  return `/api/v1/requests?${params.toString()}`;
}

/** Número de solicitudes propias en un estado: una página de un elemento y su `total`. */
export function useMyRequestCount(status: RequestStatus) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', 'mine', 'count', status],
    queryFn: async () => (await api.get(listPath({ status, pageSize: 1 }), RequestPageSchema)).total,
  });
}

/**
 * Total de un ámbito (W6, panel del responsable): una página de un elemento (`pageSize=1`) de la que solo se
 * usa `total`, p. ej. las pendientes asignadas (`scope=approvals&status=PENDING_APPROVAL`) o las del equipo.
 */
export function useRequestCount(scope: ListScope, status?: RequestStatus) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', scope, 'count', status ?? 'all'],
    queryFn: async () => (await api.get(listPath({ scope, status, pageSize: 1 }), RequestPageSchema)).total,
  });
}

/**
 * Una página de un ámbito (propias, aprobaciones, equipo o todas). Los filtros de H-02 van solo si tienen
 * valor; el ámbito lo sigue decidiendo la API. Los contadores del inicio y del panel no usan estos filtros.
 */
export function useScopedRequestsPage(scope: ListScope, query: RequestPageQuery) {
  const api = useApi();
  const path = listPath({ ...query, scope, pageSize: MY_REQUESTS_PAGE_SIZE });
  return useQuery({
    queryKey: ['requests', scope, 'page', path],
    queryFn: () => api.get(path, RequestPageSchema),
  });
}

/** Tamaño de página del calendario del equipo: el máximo que admite la API. */
export const TEAM_CALENDAR_PAGE_SIZE = 100;

/**
 * Todas las solicitudes del equipo directo con fecha del permiso entre `from` y `to` (inclusivas), en cualquier
 * estado: el calendario no usa los filtros ni la página de la lista. Si `total` supera una página, pide las
 * siguientes del mismo intervalo hasta tenerlas todas.
 */
export function useTeamCalendarRequests(from: string, to: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', 'team', 'calendar', from, to],
    queryFn: async () => {
      const items: PermissionRequestSummary[] = [];
      for (let page = 1; ; page += 1) {
        const result = await api.get(listPath({ scope: 'team', from, to, page, pageSize: TEAM_CALENDAR_PAGE_SIZE }), RequestPageSchema);
        items.push(...result.items);
        // Una página vacía corta el bucle aunque `total` cambie entre lecturas.
        if (result.items.length === 0 || items.length >= result.total) return items;
      }
    },
  });
}

export function useRequest(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', 'detail', id],
    queryFn: () => api.get(`/api/v1/requests/${encodeURIComponent(id)}`, RequestSchema),
  });
}

export function useRequestHistory(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['requests', 'history', id],
    queryFn: () => api.get(`/api/v1/requests/${encodeURIComponent(id)}/history`, HistorySchema),
  });
}

// ---------------------------------------------------------------- W3: crear, editar, enviar y cancelar

/** Tras un cambio: la solicitud devuelta pasa a la caché y se vuelven a pedir historial, listado y contadores. */
function useAfterChange() {
  const queryClient = useQueryClient();
  return (request: PermissionRequest) => {
    queryClient.setQueryData(['requests', 'detail', request.id], request);
    void queryClient.invalidateQueries({ queryKey: ['requests', 'history', request.id] });
    void queryClient.invalidateQueries({ queryKey: ['requests', 'mine'] });
    void queryClient.invalidateQueries({ queryKey: ['requests', 'approvals'] });
    void queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
  };
}

/** 412 (otra versión) o 409 `INVALID_TRANSITION` (otro estado): se vuelve a leer la solicitud. */
function useReloadOnConflict() {
  const queryClient = useQueryClient();
  return (id: string, error: unknown) => {
    if (error instanceof ApiProblem && (error.status === 412 || (error.status === 409 && error.code === 'INVALID_TRANSITION'))) {
      void queryClient.invalidateQueries({ queryKey: ['requests', 'detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['requests', 'history', id] });
    }
  };
}

/** `POST /requests` con la `Idempotency-Key` de la intención. La solicitud nace siempre en `DRAFT`. */
const postRequest = (api: ApiClient, intent: IntentKey, body: RequestCreate) =>
  withIntent(intent, body, (key) => api.mutate('POST', '/api/v1/requests', body, { schema: RequestSchema, headers: { 'Idempotency-Key': key } }));

/** `POST /requests/{id}/submit`, sin cuerpo, con `If-Match` de la versión y la `Idempotency-Key` de la intención. */
const postSubmit = (api: ApiClient, intent: IntentKey, id: string, version: number) =>
  withIntent(intent, { submit: id, version }, (key) =>
    api.mutate('POST', `/api/v1/requests/${encodeURIComponent(id)}/submit`, undefined, {
      schema: RequestSchema,
      headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
    }),
  );

/** Lo que manda el formulario de alta: la solicitud y, si se eligió, el documento (no va en `POST /requests`). */
export interface CreateInput {
  readonly body: RequestCreate;
  readonly file?: File | undefined;
}

/** Paso posterior al alta que puede fallar con el borrador ya creado. */
export type AfterCreateStep = 'document' | 'submit';

/**
 * Resultado del alta y de sus pasos. Si el alta falla, la mutación falla (el error se queda en el formulario).
 * Si el alta responde, el borrador ya existe: `failure` dice qué paso posterior falló (el documento o el envío)
 * y con qué error (Problem Details o sin respuesta); `request` es la última versión que devolvió la API.
 */
export type CreateResult =
  | { readonly request: PermissionRequest; readonly failure?: undefined }
  | { readonly request: PermissionRequest; readonly failure: { readonly step: AfterCreateStep; readonly error: unknown } };

/**
 * Alta desde el formulario, en una sola pulsación (la API no crea una solicitud con documento ni ya enviada):
 * 1. `POST /requests` con la `Idempotency-Key` de esta intención;
 * 2. con el 201 y un archivo elegido, `POST /requests/{id}/documents` (`postDocument`, como el detalle);
 * 3. si `submit`, `POST /requests/{id}/submit` con `If-Match` de la última versión (la del documento, si se
 *    adjuntó: adjuntar cambia la versión) y otra `IntentKey`, así que las claves no coinciden.
 * Si falla el documento, no se envía. No usa `useAttachDocument` ni `useSubmitRequest`: el id solo existe cuando
 * el alta responde.
 */
function useCreateSteps(submit: boolean) {
  const api = useApi();
  const createIntent = useIntentKey();
  const submitIntent = useIntentKey();
  const afterChange = useAfterChange();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, file }: CreateInput): Promise<CreateResult> => {
      let current = await postRequest(api, createIntent, body);
      afterChange(current);
      const failed = (step: AfterCreateStep, error: unknown): CreateResult => {
        // Sin respuesta, la llamada pudo llegar a la API: el detalle vuelve a leer la solicitud.
        void queryClient.invalidateQueries({ queryKey: ['requests', 'detail', current.id] });
        void queryClient.invalidateQueries({ queryKey: ['requests', 'history', current.id] });
        return { request: current, failure: { step, error } };
      };
      if (file) {
        try {
          current = await postDocument(api, current.id, file);
          afterChange(current);
        } catch (error) {
          return failed('document', error);
        }
      }
      if (!submit) return { request: current };
      try {
        current = await postSubmit(api, submitIntent, current.id, current.version);
        afterChange(current);
        return { request: current };
      } catch (error) {
        return failed('submit', error);
      }
    },
  });
}

/** «Guardar borrador» en el alta: `POST /requests` y, si se eligió, el documento. */
export const useCreateRequest = () => useCreateSteps(false);

/** «Enviar para aprobación» en el alta: `POST /requests`, el documento si se eligió, y `/submit`. */
export const useCreateAndSubmitRequest = () => useCreateSteps(true);

/** `PATCH /requests/{id}` con `If-Match` (la API no pide `Idempotency-Key` aquí). */
export function useUpdateRequest(id: string) {
  const api = useApi();
  const afterChange = useAfterChange();
  const reload = useReloadOnConflict();
  return useMutation({
    mutationFn: ({ version, changes }: { version: number; changes: RequestUpdate }) =>
      api.mutate('PATCH', `/api/v1/requests/${encodeURIComponent(id)}`, changes, { schema: RequestSchema, headers: { 'If-Match': ifMatch(version) } }),
    onSuccess: afterChange,
    onError: (error) => reload(id, error),
  });
}

/** `POST /requests/{id}/submit` con `If-Match` e `Idempotency-Key`. */
export function useSubmitRequest(id: string) {
  const api = useApi();
  const intent = useIntentKey();
  const afterChange = useAfterChange();
  const reload = useReloadOnConflict();
  return useMutation({
    mutationFn: (version: number) => postSubmit(api, intent, id, version),
    onSuccess: afterChange,
    onError: (error) => reload(id, error),
  });
}

/** `POST /requests/{id}/cancel` con `If-Match` e `Idempotency-Key`; el comentario es opcional. */
export function useCancelRequest(id: string) {
  const api = useApi();
  const intent = useIntentKey();
  const afterChange = useAfterChange();
  const reload = useReloadOnConflict();
  return useMutation({
    mutationFn: ({ version, comment }: { version: number; comment: string }) => {
      const body: RequestCancel | undefined = comment.trim() === '' ? undefined : { comment: comment.trim() };
      return withIntent(intent, { cancel: id, version, body }, (key) =>
        api.mutate('POST', `/api/v1/requests/${encodeURIComponent(id)}/cancel`, body, {
          schema: RequestSchema,
          headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
        }),
      );
    },
    onSuccess: afterChange,
    onError: (error) => reload(id, error),
  });
}

// ---------------------------------------------------------------- W4: decisión del aprobador

/**
 * `POST /requests/{id}/decision` con `If-Match` e `Idempotency-Key` (clave por intención: aprobar y
 * rechazar, o rechazar con otro motivo, son intenciones distintas). Un 409 (`INVALID_TRANSITION` o
 * `REQUEST_OVERLAP`) o un 412 vuelve a leer la solicitud y su historial.
 */
export function useDecideRequest(id: string) {
  const api = useApi();
  const intent = useIntentKey();
  const afterChange = useAfterChange();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ version, decision }: { version: number; decision: Decision }) =>
      withIntent(intent, { decide: id, version, decision }, (key) =>
        api.mutate('POST', `/api/v1/requests/${encodeURIComponent(id)}/decision`, decision, {
          schema: RequestSchema,
          headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
        }),
      ),
    onSuccess: afterChange,
    onError: (error) => {
      if (error instanceof ApiProblem && (error.status === 409 || error.status === 412)) {
        void queryClient.invalidateQueries({ queryKey: ['requests', 'detail', id] });
        void queryClient.invalidateQueries({ queryKey: ['requests', 'history', id] });
      }
    },
  });
}

// ---------------------------------------------------------------- W9: acciones del administrador

/**
 * `PUT /requests/{id}/actual-duration` con `{ actualHours }`, `If-Match` e `Idempotency-Key`. La duración la
 * escribe el administrador (validada con `ActualDurationSchema`); la que se muestra después es la que
 * devuelve la API. Un 412 vuelve a leer la solicitud.
 */
export function useSetActualDuration(id: string) {
  const api = useApi();
  const intent = useIntentKey();
  const afterChange = useAfterChange();
  const reload = useReloadOnConflict();
  return useMutation({
    mutationFn: ({ version, body }: { version: number; body: ActualDuration }) =>
      withIntent(intent, { duration: id, version, body }, (key) =>
        api.mutate('PUT', `/api/v1/requests/${encodeURIComponent(id)}/actual-duration`, body, {
          schema: RequestSchema,
          headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
        }),
      ),
    onSuccess: afterChange,
    onError: (error) => reload(id, error),
  });
}

/**
 * `POST /requests/{id}/approver` con `{ approverId }`, `If-Match` e `Idempotency-Key`. Si la persona es
 * válida lo decide la API (`APPROVER_INVALID`, 422). Un 412 vuelve a leer la solicitud.
 */
export function useReassignApprover(id: string) {
  const api = useApi();
  const intent = useIntentKey();
  const afterChange = useAfterChange();
  const reload = useReloadOnConflict();
  return useMutation({
    mutationFn: ({ version, body }: { version: number; body: ReassignApprover }) =>
      withIntent(intent, { approver: id, version, body }, (key) =>
        api.mutate('POST', `/api/v1/requests/${encodeURIComponent(id)}/approver`, body, {
          schema: RequestSchema,
          headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
        }),
      ),
    onSuccess: afterChange,
    onError: (error) => reload(id, error),
  });
}
